import { computed, Injectable, signal } from '@angular/core'
import { ActivatedRouteSnapshot, ResolveEnd, Router } from '@angular/router'
import { filter } from 'rxjs'
import { ShortcutActionService } from './shortcut-action.service'
import { ShortcutPreferencesService } from './shortcut-preferences.service'
import { buildKeyMap, effectiveList, KeyMap } from './shortcut-resolver'
import { RegisteredCommand, ScopeId, ShortcutCommand } from './shortcut.types'

/**
 * Holds every registered command and the active scope stack
 * (global -> route screen -> open modals), and derives the effective key map.
 */
@Injectable({ providedIn: 'root' })
export class ShortcutRegistryService {
    private readonly commandsSig = signal<ReadonlyMap<ScopeId, ReadonlyArray<RegisteredCommand>>>(new Map())

    private readonly modalsSig = signal<Array<ScopeId>>([])

    /** Route screen from the deepest route with `data.screen`. */
    readonly screen = signal<ScopeId | null>(null)

    readonly modals = this.modalsSig.asReadonly()

    readonly commands = this.commandsSig.asReadonly()

    readonly keyMap = computed<KeyMap>(() => buildKeyMap({
        screen: this.screen(),
        modals: this.modalsSig(),
        commands: this.commandsSig(),
        prefs: this.prefs.prefs(),
        actionRunner: b => this.actions.runnerFor(b)
    }))

    /**
     * Keys each command currently answers to on this screen (it must win its combo), for UI
     * hints such as tooltips. Missing = no working shortcut (disabled, unbound or overridden).
     */
    readonly keysByCommand = computed(() => {
        const out = new Map<string, string>()
        for (const [keys, candidates] of buildKeyMap({
            screen: this.screen(),
            modals: [],
            commands: this.commandsSig(),
            prefs: this.prefs.prefs(),
            actionRunner: b => this.actions.runnerFor(b)
        })) {
            const winner = candidates[0]
            if (winner && !out.has(winner.commandId)) out.set(winner.commandId, keys)
        }
        return out
    })

    /** Effective shortcuts of the current screen, ignoring open modals (for the cheat-sheet). */
    readonly screenEffective = computed(() => effectiveList(buildKeyMap({
        screen: this.screen(),
        modals: [],
        commands: this.commandsSig(),
        prefs: this.prefs.prefs(),
        actionRunner: b => this.actions.runnerFor(b)
    })))

    constructor (router: Router, private prefs: ShortcutPreferencesService, private actions: ShortcutActionService) {
        // ResolveEnd (not NavigationEnd): the new screen must be active before its
        // components are constructed and call injectShortcuts.
        router.events.pipe(filter((e): e is ResolveEnd => e instanceof ResolveEnd)).subscribe(e => {
            this.screen.set(screenOf(e.state.root))
        })
    }

    /** Registers commands under a scope. Returns the unregister function. */
    register (scope: ScopeId, commands: ReadonlyArray<ShortcutCommand>): () => void {
        const tagged: Array<RegisteredCommand> = commands.map(c => ({ ...c, scope }))
        this.commandsSig.update(map => {
            const next = new Map(map)
            next.set(scope, [...(next.get(scope) ?? []), ...tagged])
            return next
        })

        return () => this.commandsSig.update(map => {
            const next = new Map(map)
            const left = (next.get(scope) ?? []).filter(c => !tagged.includes(c))
            if (left.length) next.set(scope, left)
            else next.delete(scope)
            return next
        })
    }

    /** Pushes a modal scope on top of the stack. Returns the pop function. */
    pushModal (scope: ScopeId): () => void {
        this.modalsSig.update(list => [...list, scope])
        return () => this.modalsSig.update(list => {
            const i = list.lastIndexOf(scope)
            return i === -1 ? list : [...list.slice(0, i), ...list.slice(i + 1)]
        })
    }

    /** Runs a registered command by id, if it's in an active scope. */
    runCommand (id: string): boolean {
        const active = new Set<ScopeId>(['global', ...this.modalsSig()])
        const screen = this.screen()
        if (screen) active.add(screen)
        for (const [scope, list] of this.commandsSig()) {
            if (!active.has(scope)) continue
            const cmd = list.find(c => c.id === id)
            if (cmd && (!cmd.when || cmd.when())) {
                cmd.run()
                return true
            }
        }
        return false
    }
}

export function screenOf (root: ActivatedRouteSnapshot): ScopeId | null {
    let screen: ScopeId | null = null
    let node: ActivatedRouteSnapshot | null = root
    while (node) {
        if (node.data?.['screen']) screen = node.data['screen']
        node = node.firstChild
    }
    return screen
}
